import { useQuery, useLazyQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_ORAL, { adaptAddVisitaOralInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaOral";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaOral({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);
  const { visita, setVisita } = useVisita();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);

  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));
  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);     

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });
  
  useEffect(() => {
    if (!dataSolicitudPendiente) return;
        setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado!=null);
  }, [dataSolicitudPendiente]);  

  const [save] = useMutation(SAVE_PROGRAMA_ORAL);

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        if (input.name === "justificacionModificacion") {
          newInput.required = cambioIndicacion;
        }

        if (input.name === "complicaciones") {
          const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
          newInput.options = complicacionesCombo?.map((opt) => {
            if (Array.isArray(formData.complicaciones)) {
              const hasNinguna = formData.complicaciones.includes(ningunaId);
              const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
              if (hasNinguna) {
                // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                return { ...opt, disabled: opt.id !== ningunaId };
              }
              if (hasOther) {
                // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                return { ...opt, disabled: opt.id === ningunaId };
              }
            }
            return { ...opt, disabled: false };
          });
        }

        if (readOnly) newInput.disabled = true;
        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, combosRI, complicacionesCombo, formData?.complicaciones, cambioIndicacion]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    setFormData((prev) => {
      const req = Number(prev.caloricoTotal);
      const cub = Number(prev.caloricoSO);
      const pct = req > 0 && !Number.isNaN(cub) ? Math.round((cub / req) * 100) : 0;

      if (pct === prev.porcentajeSO) return prev;
      return { ...prev, porcentajeSO: pct };
    });
  }, [formData.caloricoTotal, formData.caloricoSO]);

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaOralInput(formData) } });

    const idVisitaOral = data?.saveVisitaOral?.idVisitaOral;
    if (!idVisitaOral) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaOral }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);

      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaOral: visita.idVisitaOral,
        idVisita: visita.idVisita
      });

    }
  }, [visita]);

    useEffect(() => {
      if (!visita?.idEvaluacion) return;
      
      getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
    }, [visita?.idEvaluacion]);

  useEffect(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalCalorias = 0;

    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const calorias = Number(indicacion.calorias) || 0;
      totalCalorias += cantidad * calorias;
    });

    setFormData((prev) => ({ ...prev, caloricoSO: totalCalorias }));
  }, [formData.listaIndicacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaOral",
      stepName: "Programa Oral",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
            {formattedGroups.
      filter((group) => !group.hidden).
      map((group, idx) =>
      <CardBox key={idx} title={group.title}>
                        <Grid container spacing={2}>
                            {group.inputs.
          filter((input) => !input.hidden).
          map((input) =>
          <Grid size={{ xs: 12 }} key={input.name}>
                                        {input.name !== "listaIndicacion" ?
            <RenderInputByTypeMobile
              input={input}
              value={formData[input.name]}
              handleChange={handleChange}
              row={input.type === "multiple_boolean"} /> :


            <IndicacionesVisitaInput
              readOnly={readOnly}
              handleChange={handleChange}
              indicaciones={formData[input.name]}
              idEvaluacion={visita?.idEvaluacion}
              inputName={input.name} 
              solicitudIndicacionPendiente={solicitudIndicacionPendiente}
            />

            }
                                    </Grid>
          )}
                        </Grid>
                    </CardBox>
      )}
        </>);

}

const groups = [
{
  title: "",
  inputs: [
  {
    name: "idComboRIValorKarnofsky",
    displayName: "Karnofsky",
    comboName: "Karnofsky",
    type: "select",
    required: true
  }]

},
{
  title: "Indicaciones",
  inputs: [
  {
    name: "listaIndicacion",
    displayName: "Indicaciones (productos)",
    type: "custom",
    required: true
  },
  {
    name: "justificacionModificacion",
    displayName: "Justificación por modificación",
    type: "long_string",
    maxLength: 4000
  },
  {
    name: "indicacionComentario",
    displayName: "Comentarios de indicación",
    type: "long_string",
    maxLength: 4000
  }]

},
{
  title: "Requerimientos",
  inputs: [
  {
    name: "caloricoTotal",
    displayName: "Requerimiento calórico total",
    type: "float",
    required: true
  },
  {
    name: "caloricoSO",
    displayName: "Calorías cubiertas por SO (totales)",
    type: "float",
    required: true
  },
  {
    name: "porcentajeSO",
    displayName: "%SO",
    type: "int",
    slotProps:{ inputLabel: { shrink: true } }
  },
  {
    name: "complicaciones",
    displayName: "Complicaciones",
    type: "multiple_select",
    required: true
  }]

}];